import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    HelpCircle,
    Mail,
    MessageCircle,
    Search,
    ChevronDown,
    ChevronUp,
    ThumbsUp,
    ThumbsDown,
    Eye,
    BookOpen,
    Zap,
    Users,
    Globe,
    Sparkles,
    CheckCircle,
    ArrowRight,
    Star,
    TrendingUp,
} from "lucide-react";
import BackToTop from "../../components/BackToTop/BackToTop";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import HeroBg from "../../../src/images/hero-bg.png";

const Support = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [activeCategory, setActiveCategory] = useState("all");
    const [expandedFaq, setExpandedFaq] = useState(null);
    const [feedback, setFeedback] = useState({});
    const [searchFocused, setSearchFocused] = useState(false);

    const categories = [
        { id: "all", name: "All Questions", icon: HelpCircle, color: "gray" },
        { id: "getting-started", name: "Getting Started", icon: Zap, color: "blue" },
        { id: "account", name: "Account & Billing", icon: Users, color: "green" },
        { id: "technical", name: "Technical", icon: Globe, color: "purple" },
        { id: "tutorials", name: "Tutorials", icon: BookOpen, color: "orange" },
    ];

    const faqs = [
        {
            id: 1,
            category: "getting-started",
            question: "How do I create my first CTV ad?",
            answer: "Creating your first CTV ad is simple:\n\n1. Sign up for a free account at SocialTV\n2. Click 'Create New Ad' in your dashboard\n3. Choose a template from our library\n4. Paste your social media video URL\n5. Customize with your brand colors and logo\n6. Preview your ad and generate a VAST tag\n\nYour ad will be ready in under 5 minutes!",
            views: 1234,
            helpful: 89,
            trending: true
        },
        {
            id: 2,
            category: "getting-started",
            question: "What platforms can I import content from?",
            answer: "We support imports from all major social platforms:\n\n• Instagram (Reels, Posts, Stories)\n• TikTok\n• Facebook (Videos, Reels)\n• X (Twitter)\n• Pinterest\n• YouTube\n• Vimeo\n\nSimply paste the video URL and our AI will automatically optimize it for TV display.",
            views: 2341,
            helpful: 95,
            popular: true
        },
        {
            id: 3,
            category: "account",
            question: "How do I cancel my subscription?",
            answer: "To cancel your subscription:\n\n1. Log into your SocialTV account\n2. Go to Settings > Billing\n3. Click 'Manage Subscription'\n4. Select 'Cancel Subscription'\n5. Confirm cancellation\n\nYour access will continue until the end of your current billing period. No further charges will be made.",
            views: 3456,
            helpful: 92,
            trending: true
        },
        {
            id: 4,
            category: "account",
            question: "What payment methods do you accept?",
            answer: "We accept all major payment methods:\n\n• Credit/Debit Cards (Visa, MasterCard, American Express)\n• PayPal\n• Bank transfers (for annual enterprise plans)\n• Apple Pay\n• Google Pay\n\nAll payments are processed securely through Stripe.",
            views: 1567,
            helpful: 88
        },
        {
            id: 5,
            category: "technical",
            question: "What is a VAST tag and how do I use it?",
            answer: "VAST (Video Ad Serving Template) is a universal format that allows video ads to be served across different platforms.\n\nTo use your VAST tag:\n\n1. Create your ad in SocialTV\n2. Copy the VAST tag URL from your dashboard\n3. Paste it into your ad server (Google Ad Manager, The Trade Desk, etc.)\n4. The ad server will pull your video and display it on CTV platforms\n\nYour VAST tag is compatible with all major platforms including Roku, Amazon Fire TV, and Samsung TV Plus.",
            views: 4321,
            helpful: 96,
            popular: true,
            trending: true
        },
        {
            id: 6,
            category: "technical",
            question: "What video formats and resolutions do you support?",
            answer: "We support the following formats:\n\n• Video: MP4, MOV, AVI\n• Resolution: Up to 4K (3840x2160)\n• Aspect Ratios: 16:9, 9:16, 1:1, 4:5\n• Frame Rate: Up to 60fps\n• Audio: AAC, MP3\n\nOur platform automatically optimizes your video for the best quality on TV screens.",
            views: 2876,
            helpful: 91
        },
        {
            id: 7,
            category: "tutorials",
            question: "How do I add my logo and branding?",
            answer: "Adding your branding is easy:\n\n1. In the ad editor, click 'Branding'\n2. Upload your logo (PNG or SVG recommended)\n3. Choose logo position\n4. Select your brand colors for text overlays\n5. Preview to see how it looks\n\nYour branding will be automatically applied to all your ads.",
            views: 1987,
            helpful: 94,
            popular: true
        },
        {
            id: 8,
            category: "tutorials",
            question: "Can I schedule my ads to run at specific times?",
            answer: "Yes! With our Pro and Enterprise plans, you can:\n\n• Set start and end dates for campaigns\n• Schedule ads for specific times of day\n• Target specific days of the week\n• Set frequency caps per user\n• Create custom schedules for different platforms",
            views: 1654,
            helpful: 93
        }
    ];

    const filteredFaqs = faqs.filter(faq => {
        const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
        const matchesSearch = searchQuery === "" ||
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesSearch;
    });

    const handleFaqClick = (id) => {
        setExpandedFaq(expandedFaq === id ? null : id);
    };

    const handleFeedback = (faqId, type) => {
        setFeedback(prev => ({
            ...prev,
            [faqId]: type
        }));
    };

    const getCategoryColor = (categoryId) => {
        const colors = {
            "getting-started": "bg-blue-100 text-blue-700 border-blue-200",
            "account": "bg-green-100 text-green-700 border-green-200",
            "technical": "bg-purple-100 text-purple-700 border-purple-200",
            "tutorials": "bg-orange-100 text-orange-700 border-orange-200"
        };
        return colors[categoryId] || "bg-gray-100 text-gray-700 border-gray-200";
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white">
            <BackToTop />

            {/* ================= HEADER ================= */}
            <Header />

            <div className="relative">

                <div className="absolute inset-0 w-full h-full">
                    <img
                        src={HeroBg}
                        loading="lazy"
                        sizes="(max-width: 1440px) 100vw, 1440px"
                        alt="hero"
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-0 -left-40 w-[400px] h-[400px] bg-gradient-to-r from-indigo-200/20 via-purple-200/20 to-pink-200/20 rounded-full blur-3xl animate-float-slow"></div>
                    <div className="absolute bottom-0 -right-40 w-[400px] h-[400px] bg-gradient-to-l from-pink-200/20 via-purple-200/20 to-indigo-200/20 rounded-full blur-3xl animate-float-slower"></div>

                    {[...Array(50)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute w-1 h-1 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full animate-float-particle"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 8}s`,
                                animationDuration: `${15 + Math.random() * 15}s`,
                                opacity: 0.2,
                            }}
                        />
                    ))}
                </div> */}

                {/* Main Content */}
                <main className="max-w-5xl mx-auto px-6 py-20 relative">
                    {/* Hero with animation */}
                    <div className="text-center my-16 animate-fade-in">
                        <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full mb-6 hover:shadow-md transition-shadow">
                            <Sparkles className="h-4 w-4 text-indigo-600" />
                            <span className="text-sm font-medium text-indigo-600">24/7 Support</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            How can we{' '}
                            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                help you?
                            </span>
                        </h1>
                        <p className="text-lg text-gray-500 max-w-2xl mx-auto">
                            Search our help center or browse the topics below to find answers
                        </p>
                    </div>

                    {/* Search with focus effect */}
                    <div className="max-w-2xl mx-auto mb-16">
                        <div className={`relative transition-all duration-300 ${searchFocused ? 'scale-105' : ''}`}>
                            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors duration-300 ${searchFocused ? 'text-indigo-600' : 'text-gray-400'}`} />
                            <input
                                type="text"
                                placeholder="Search for answers..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                                className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm hover:shadow-md transition-all"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery("")}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    </div>



                    {/* FAQ Section */}
                    <div className="mb-16">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                            <h2 className="text-xl font-semibold text-gray-900">Frequently asked questions</h2>

                            {/* Category Tabs with animation */}
                            {/* <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setActiveCategory(category.id)}
                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-2 ${activeCategory === category.id
                                        ? "bg-white text-indigo-600 shadow-md"
                                        : "text-gray-600 hover:text-indigo-600"
                                        }`}
                                >
                                    <category.icon className="h-4 w-4" />
                                    <span className="hidden sm:inline">{category.name}</span>
                                </button>
                            ))}
                        </div> */}
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                            {/* <h2 className="text-lg font-semibold text-gray-900">Frequently asked questions</h2> */}

                            {/* Category Tabs with icons and text always visible */}
                            <div className="flex flex-wrap gap-3">
                                {categories.map((category) => (
                                    <button
                                        key={category.id}
                                        onClick={() => setActiveCategory(category.id)}
                                        className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-300 flex items-center gap-2 ${activeCategory === category.id
                                            ? "border border-transparent bg-indigo-600 text-white shadow-lg shadow-indigo-200 "
                                            : "bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/50"
                                            }`}
                                    >
                                        <category.icon className={`h-4 w-4 ${activeCategory === category.id ? "text-white" : "text-gray-500"
                                            }`} />
                                        <span>{category.name}</span>

                                        {/* Optional: Add count badge */}
                                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === category.id
                                            ? "bg-white/20 text-white"
                                            : "bg-gray-100 text-gray-500"
                                            }`}>
                                            {faqs.filter(f => f.category === category.id || category.id === "all").length}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* FAQ Accordion with animations */}
                        <div className="space-y-5">
                            {filteredFaqs.length > 0 ? (
                                filteredFaqs.map((faq) => (
                                    <div
                                        key={faq.id}
                                        className="border border-gray-200 rounded-xl overflow-hidden hover:border-indigo-200 transition-all duration-300 hover:shadow-md"
                                    >
                                        <button
                                            onClick={() => handleFaqClick(faq.id)}
                                            className="w-full px-6 py-4 text-left flex items-center justify-between gap-4 bg-white transition-colors group"
                                        >
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(faq.category)}`}>
                                                        {categories.find(c => c.id === faq.category)?.name}
                                                    </span>
                                                    {faq.trending && (
                                                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                            <TrendingUp className="h-3 w-3" /> Trending
                                                        </span>
                                                    )}
                                                    {faq.popular && (
                                                        <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                            <Star className="h-3 w-3 fill-purple-600" /> Popular
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="font-medium text-gray-900 group-hover:text-indigo-600 transition-colors">
                                                    {faq.question}
                                                </span>
                                            </div>
                                            <div className={`p-1 rounded-full transition-all duration-300 ${expandedFaq === faq.id
                                                ? 'bg-indigo-100 rotate-180'
                                                : 'bg-gray-100 group-hover:bg-indigo-100'
                                                }`}>
                                                {expandedFaq === faq.id ? (
                                                    <ChevronUp className="h-5 w-5 text-indigo-600" />
                                                ) : (
                                                    <ChevronDown className="h-5 w-5 text-gray-500 group-hover:text-indigo-600" />
                                                )}
                                            </div>
                                        </button>

                                        {/* Expandable answer with animation */}
                                        <div className={`transition-all duration-300 overflow-hidden ${expandedFaq === faq.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                                            }`}>
                                            {expandedFaq === faq.id && (
                                                <div className="px-6 pb-6 border-t border-gray-100 bg-white">
                                                    <div className="pt-4">
                                                        <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                                                            {faq.answer}
                                                        </p>

                                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                                                            <div className="flex items-center gap-4">
                                                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                                                    <Eye className="h-4 w-4" />
                                                                    {faq.views.toLocaleString()} views
                                                                </div>
                                                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                                                    <ThumbsUp className="h-4 w-4" />
                                                                    {faq.helpful}% helpful
                                                                </div>
                                                            </div>

                                                            <div className="flex items-center gap-3">
                                                                <span className="text-sm text-gray-500">Was this helpful?</span>
                                                                <div className="flex gap-2">
                                                                    <button
                                                                        onClick={() => handleFeedback(faq.id, 'yes')}
                                                                        className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-110 ${feedback[faq.id] === 'yes'
                                                                            ? 'bg-green-100 text-green-600'
                                                                            : 'bg-white border border-gray-200 text-gray-400 hover:bg-green-50 hover:text-green-600 hover:border-green-200'
                                                                            }`}
                                                                    >
                                                                        <ThumbsUp className="h-4 w-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleFeedback(faq.id, 'no')}
                                                                        className={`p-2 rounded-lg transition-all duration-300 transform hover:scale-110 ${feedback[faq.id] === 'no'
                                                                            ? 'bg-red-100 text-red-600'
                                                                            : 'bg-white border border-gray-200 text-gray-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                                                                            }`}
                                                                    >
                                                                        <ThumbsDown className="h-4 w-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Feedback thank you message */}
                                                        {feedback[faq.id] && (
                                                            <div className="mt-4 flex items-center gap-2 text-sm text-green-600 animate-fade-in">
                                                                <CheckCircle className="h-4 w-4" />
                                                                Thank you for your feedback!
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200">
                                    <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                    <p className="text-gray-600">No results found for "{searchQuery}"</p>
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="mt-2 text-indigo-600 hover:text-indigo-700 hover:underline transition-all"
                                    >
                                        Clear search
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Contact Options with hover effects */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <Link
                            to="/contact"
                            className="group p-8 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl text-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
                        >
                            {/* Animated background effect */}
                            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>

                            <MessageCircle className="h-8 w-8 mb-4 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                            <h3 className="text-xl font-semibold mb-2 relative z-10">Contact support</h3>
                            <p className="text-indigo-100 mb-4 relative z-10">Get help from our support team</p>
                            <span className="inline-flex items-center gap-1 text-white/90 group-hover:text-white relative z-10">
                                Send message <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </Link>

                        <a
                            href="mailto:support@socialtv.com"
                            className="group p-8 bg-white border border-gray-200 rounded-2xl hover:border-indigo-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                            <Mail className="h-8 w-8 text-indigo-600 mb-4 relative z-10 group-hover:scale-110 transition-transform duration-300" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2 relative z-10">Email us</h3>
                            <p className="text-gray-500 mb-4 relative z-10">support@socialtv.com</p>
                            <span className="inline-flex items-center gap-1 text-indigo-600 relative z-10">
                                Send email <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                            </span>
                        </a>
                    </div>

                    {/* Quick help footer */}
                    <div className="mt-12 text-center">
                        <p className="text-gray-400 text-sm">
                            Our support team typically responds within{' '}
                            <span className="text-gray-600 font-medium">24 hours</span>
                        </p>
                    </div>

                </main>
            </div>

            {/* ================= FOOTER ================= */}
            <Footer />

        </div>
    );
};

export default Support;